import React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CLUB_TYPES, SKILL_LEVELS, PACE_RANGES, getPaceRangeDisplayLabel } from "../../constants/clubConstants";
import PaceUnitToggle from "./PaceUnitToggle";
import GooglePlacesAddressInput from "../GooglePlacesAddressInput";
import {
  AddClubCarouselImagesSection,
  ConsoleClubCarouselPasteUrlsSection,
  ConsoleImageUrlPasteRow,
  renderInternalNotesField,
  renderSingleImageField,
} from "./ConsoleClubFormShared";
/**
 * Console "Onboard Club" dialog (email/password + club fields).
 */
export default function ClubsOnboardClubModal({
  open,
  onRequestClose,
  addClubForm,
  setAddClubForm,
  addClubSubmitting,
  onSubmit,
  showOnboardPassword,
  setShowOnboardPassword,
  heroImageFile,
  setHeroImageFile,
  profileImageFile,
  setProfileImageFile,
  carouselImageFiles,
  setCarouselImageFiles,
  addClubHeroPasteUrl,
  setAddClubHeroPasteUrl,
  addClubProfilePasteUrl,
  setAddClubProfilePasteUrl,
  addClubCarouselPasteUrls,
  setAddClubCarouselPasteUrls,
  maxCarouselPasteUrls = 0,
  setImagePreviewUrls,
  consolePaceUnit,
}) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onRequestClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Onboard Club</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create a run club account (email + password only) and club with the same fields as mobile. Organizer can log in and edit profile in the app.
          </p>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Organizer (email + password only)</h4>
              <p className="text-xs text-muted-foreground">
                Required fields marked with <span className="text-destructive">*</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Convention: use the run club name in the email, ending with <strong>@gmail.com</strong> (e.g. clubname@gmail.com). Password: start with a capital letter, end in <strong>1</strong>.
              </p>
              <div className="grid gap-2">
                <label htmlFor="onboard-email" className="text-sm font-medium">
                  Email <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <Input
                  id="onboard-email"
                  type="email"
                  value={addClubForm.email}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="e.g. clubname@gmail.com"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-password" className="text-sm font-medium">
                  Password <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="onboard-password"
                    type={showOnboardPassword ? "text" : "password"}
                    value={addClubForm.password}
                    onChange={(e) => setAddClubForm((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Capital letter, end in 1 (min 8 chars)"
                    required
                    autoComplete="new-password"
                    className="pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOnboardPassword(!showOnboardPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showOnboardPassword ? "Hide password" : "Show password"}
                  >
                    {showOnboardPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Club (same fields as mobile create/edit)</h4>
              <p className="text-xs text-muted-foreground">
                <span className="text-destructive">*</span> Required to submit. Fields below marked <span className="text-destructive">*</span> are also required for your club to go public (discoverable): description (10+ chars), type, skill level, meet-up address or city+state, profile image, hero image.
              </p>
              <div className="grid gap-2">
                <label htmlFor="onboard-run-club-name" className="text-sm font-medium">
                  Club name <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <Input
                  id="onboard-run-club-name"
                  value={addClubForm.run_club_name}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, run_club_name: e.target.value }))}
                  placeholder="Club name"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-city" className="text-sm font-medium">
                  City <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <Input
                  id="onboard-city"
                  value={addClubForm.city}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="e.g. Paris"
                  required
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Use one canonical name per market (e.g. <strong>Paris</strong>), not “City, Country”, so console filters match.
                </p>
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-state" className="text-sm font-medium">
                  State / region
                </label>
                <Input
                  id="onboard-state"
                  value={addClubForm.state}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, state: e.target.value }))}
                  placeholder="e.g. FL, NY, or IDF (optional)"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-country" className="text-sm font-medium">
                  Country
                </label>
                <Input
                  id="onboard-country"
                  value={addClubForm.country}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, country: e.target.value }))}
                  placeholder="e.g. France, United States (optional)"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Optional. For international clubs; often filled when you choose a meet-up address from Google Places.
                </p>
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-type" className="text-sm font-medium">
                  Type <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <select
                  id="onboard-type"
                  value={addClubForm.type}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, type: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">—</option>
                  {CLUB_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-description" className="text-sm font-medium">
                  Description <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <Textarea
                  id="onboard-description"
                  value={addClubForm.description}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="About the club (max 1000 chars)"
                  rows={3}
                  maxLength={1000}
                  className="resize-none"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-skill-level" className="text-sm font-medium">
                  Who it&apos;s for (skill level) <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <select
                  id="onboard-skill-level"
                  value={addClubForm.skill_level}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, skill_level: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">—</option>
                  {SKILL_LEVELS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-home-base-address" className="text-sm font-medium">
                  Meet-up address <span className="text-destructive" aria-hidden="true">*</span>
                </label>
                <GooglePlacesAddressInput
                  id="onboard-home-base-address"
                  value={addClubForm.home_base_address}
                  onChange={(val) => setAddClubForm((prev) => ({ ...prev, home_base_address: val }))}
                  onPlaceSelected={(place) => {
                    const rawLat = place.latitude != null ? Number(place.latitude) : null;
                    const rawLon = place.longitude != null ? Number(place.longitude) : null;
                    const validCoords =
                      rawLat != null &&
                      rawLon != null &&
                      Number.isFinite(rawLat) &&
                      Number.isFinite(rawLon);
                    setAddClubForm((prev) => ({
                      ...prev,
                      home_base_address: place.formattedAddress ?? place.name ?? "",
                      city: place.city || prev.city,
                      state: place.state || prev.state,
                      country: (place.country && String(place.country).trim()) || prev.country,
                      latitude: validCoords ? rawLat : null,
                      longitude: validCoords ? rawLon : null,
                    }));
                  }}
                  placeholder="Address or place name"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="onboard-home-base-description" className="text-sm font-medium">
                  Meet-up description
                </label>
                <Textarea
                  id="onboard-home-base-description"
                  value={addClubForm.home_base_description}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, home_base_description: e.target.value }))}
                  placeholder="e.g. We meet at the north entrance"
                  rows={2}
                  maxLength={500}
                  className="resize-none"
                />
              </div>
              <div className="grid gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="text-sm font-medium">Pace ranges (max 3)</label>
                  <PaceUnitToggle />
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Pace ranges">
                  {PACE_RANGES.map((p) => {
                    const selected = Array.isArray(addClubForm.paces) && addClubForm.paces.includes(p.value);
                    const atMax = Array.isArray(addClubForm.paces) && addClubForm.paces.length >= 3;
                    return (
                      <Button
                        key={p.value}
                        type="button"
                        variant={selected ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const current = Array.isArray(addClubForm.paces) ? addClubForm.paces : [];
                          const next = current.includes(p.value)
                            ? current.filter((v) => v !== p.value)
                            : current.length < 3
                              ? [...current, p.value]
                              : current;
                          setAddClubForm((prev) => ({ ...prev, paces: next }));
                        }}
                        disabled={!selected && atMax}
                        className={selected ? "bg-[#1A1916] hover:bg-[#3C3830]" : ""}
                      >
                        {getPaceRangeDisplayLabel(p, consolePaceUnit)}
                      </Button>
                    );
                  })}
                </div>
                <span className="text-xs text-muted-foreground">Select up to 3 pace ranges (stored as min/mi)</span>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Club notes (max 3)</label>
                {[0, 1, 2].map((i) => (
                  <Input
                    key={i}
                    id={`onboard-club-note-${i}`}
                    value={addClubForm.club_notes?.[i] ?? ""}
                    onChange={(e) => {
                      const next = [...(addClubForm.club_notes ?? ["", "", ""])];
                      next[i] = e.target.value;
                      setAddClubForm((prev) => ({ ...prev, club_notes: next }));
                    }}
                    placeholder={`Note ${i + 1}`}
                    autoComplete="off"
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="onboard-recurring-runs-vary"
                  checked={addClubForm.recurring_runs_vary ?? false}
                  onChange={(e) => setAddClubForm((prev) => ({ ...prev, recurring_runs_vary: e.target.checked }))}
                  className="rounded border-[var(--console-border)]"
                />
                <label htmlFor="onboard-recurring-runs-vary" className="text-sm font-medium cursor-pointer">
                  Weekly runs vary (location or type)
                </label>
              </div>
              {renderSingleImageField("Profile picture", {
                required: true,
                file: profileImageFile,
                onFileChange: setProfileImageFile,
                onPreview: (url) => setImagePreviewUrls([url]),
                inputId: "onboard-profile-picture",
              })}
              <ConsoleImageUrlPasteRow
                id="onboard-profile-paste-url"
                value={addClubProfilePasteUrl}
                onChange={setAddClubProfilePasteUrl}
                disabled={Boolean(profileImageFile)}
                disabledHint="Remove the profile file above to use a pasted URL instead."
              />
              {renderSingleImageField("Hero image", {
                required: true,
                file: heroImageFile,
                onFileChange: setHeroImageFile,
                onPreview: (url) => setImagePreviewUrls([url]),
                inputId: "onboard-hero-image",
              })}
              <ConsoleImageUrlPasteRow
                id="onboard-hero-paste-url"
                value={addClubHeroPasteUrl}
                onChange={setAddClubHeroPasteUrl}
                disabled={Boolean(heroImageFile)}
                disabledHint="Remove the hero file above to use a pasted URL instead."
              />
              <AddClubCarouselImagesSection
                heroFile={heroImageFile}
                carouselFiles={carouselImageFiles}
                setCarouselFiles={setCarouselImageFiles}
                setImagePreviewUrls={setImagePreviewUrls}
                onClearHero={() => setHeroImageFile(null)}
              />
              <ConsoleClubCarouselPasteUrlsSection
                inputId="onboard-carousel-paste-url"
                pasteUrls={addClubCarouselPasteUrls}
                onPasteUrlsChange={setAddClubCarouselPasteUrls}
                maxCount={maxCarouselPasteUrls}
                disabled={addClubSubmitting}
              />
              {renderInternalNotesField({
                id: "onboard-internal-notes",
                value: addClubForm.internal_notes,
                onChange: (val) => setAddClubForm((prev) => ({ ...prev, internal_notes: val })),
              })}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onRequestClose} disabled={addClubSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="bg-[#1A1916] hover:bg-[#3C3830]" disabled={addClubSubmitting}>
              {addClubSubmitting ? "Onboarding…" : "Onboard Club"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
